import React from 'react';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import PostCard from './PostCard';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarMonthView = ({
    currentDate,
    posts,
    integrations,
    onSlotClick,
    onPostClick,
    onPostEdit,
    onPostDelete,
    onPostPublish
}) => {
    // Get the first day of the month
    const monthStart = dayjs(currentDate).startOf('month');
    const monthEnd = dayjs(currentDate).endOf('month');

    // Get the start of the calendar (may include days from previous month)
    const calendarStart = monthStart.startOf('week');
    const calendarEnd = monthEnd.endOf('week');

    // Generate all days to display
    const totalDays = calendarEnd.diff(calendarStart, 'day') + 1;
    const calendarDays = Array.from({ length: totalDays }, (_, i) =>
        calendarStart.add(i, 'day')
    );

    // Split into weeks
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
        weeks.push(calendarDays.slice(i, i + 7));
    }

    // Get integration by ID
    const getIntegration = (integrationId) => {
        return integrations.find(i => i.id === integrationId);
    };

    // Get posts for a specific day
    const getPostsForDay = (day) => {
        const dayStart = day.startOf('day');
        const dayEnd = day.endOf('day');

        return posts.filter(post => {
            if (!post.scheduled_at && !post.published_at) return false;
            const postDate = dayjs(post.scheduled_at || post.published_at);
            return postDate.isAfter(dayStart) && postDate.isBefore(dayEnd);
        });
    };

    // Check various day states
    const isToday = (day) => day.isSame(dayjs(), 'day');
    const isCurrentMonth = (day) => day.month() === dayjs(currentDate).month();

    return (
        <div className="flex flex-col h-full">
            {/* Header row with day names */}
            <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700 sticky top-0 bg-white dark:bg-slate-900 z-10">
                {DAYS.map((day) => (
                    <div
                        key={day}
                        className="p-3 text-center border-r border-slate-200 dark:border-slate-700 last:border-r-0"
                    >
                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                            {day}
                        </span>
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="flex-1 overflow-auto">
                {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700 last:border-b-0">
                        {week.map((day) => {
                            const dayPosts = getPostsForDay(day);
                            const isInCurrentMonth = isCurrentMonth(day);
                            const isTodayDate = isToday(day);

                            return (
                                <div
                                    key={day.format('YYYY-MM-DD')}
                                    onClick={() => onSlotClick?.(day.hour(10).minute(0).toISOString())}
                                    className={`min-h-[120px] border-r border-slate-200 dark:border-slate-700 last:border-r-0 p-2 cursor-pointer transition-colors ${
                                        isTodayDate
                                            ? 'bg-sky-50 dark:bg-sky-900/20'
                                            : isInCurrentMonth
                                                ? 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                                : 'bg-slate-50 dark:bg-slate-900/50'
                                    }`}
                                >
                                    {/* Day number */}
                                    <div className="flex items-center justify-between mb-2">
                                        <span
                                            className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium ${
                                                isTodayDate
                                                    ? 'bg-sky-600 text-white'
                                                    : isInCurrentMonth
                                                        ? 'text-slate-900 dark:text-white'
                                                        : 'text-slate-400 dark:text-slate-600'
                                            }`}
                                        >
                                            {day.format('D')}
                                        </span>
                                        {dayPosts.length > 0 && (
                                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                                {dayPosts.length} post{dayPosts.length !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>

                                    {/* Posts */}
                                    <div className="space-y-1">
                                        {dayPosts.slice(0, 3).map((post) => (
                                            <PostCard
                                                key={post.id}
                                                post={post}
                                                integration={getIntegration(post.integration_id)}
                                                compact
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onPostClick?.(post);
                                                }}
                                            />
                                        ))}
                                        {dayPosts.length > 3 && (
                                            <div className="text-xs text-slate-500 dark:text-slate-400 text-center py-1 bg-slate-100 dark:bg-slate-800 rounded">
                                                +{dayPosts.length - 3} more
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CalendarMonthView;
